import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { nanoid } from 'nanoid'

import { RulesApi } from 'shared/api/RulesAPI'
import EmptyImage from 'shared/images/empty_tariff.png'
import { getCleanHTML } from 'shared/lib/getCleanHTML'
import { TicketItem } from 'shared/models/ticket'
import { EmptyResults } from 'shared/ui/empty-result'
import { Loader } from 'shared/ui/loader'

import styles from './styles.module.scss'

interface Props {
  additionalTariff?: string[]
  rulesParams: Pick<TicketItem, 'tid' | 'source' | 'uid'>
}

const TariffPop = ({ rulesParams, additionalTariff }: Props) => {
  const { t } = useTranslation()

  const abortController = useRef<AbortController | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [rules, setRules] = useState<string | null>(null)

  const getRules = async () => {
    try {
      const controller = new AbortController()
      abortController.current = controller

      const { data: rulesInfo } = await RulesApi.getRule(
        {
          tid: rulesParams.tid,
          source: rulesParams.source,
          uid: rulesParams.uid,
        },
        controller.signal,
      )

      setRules(() => rulesInfo.data)
    } catch (_) {
      // Show the empty state when tariff rules cannot be loaded.
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getRules()

    return () => abortController.current?.abort()
  }, [])

  if (isLoading) {
    return (
      <div className={clsx(styles.container, styles.loading)}>
        <Loader />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {additionalTariff && (
        <div className={styles.wrapperList}>
          <p className={styles.title}>{t('tariff_additional')}</p>

          <div className={clsx('regular-14', styles.tariffWrapper)}>
            {additionalTariff?.map((tariff) => (
              <p key={nanoid()}>
                <span className={styles.circle} /> {tariff}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className={styles.wrapperList}>
        <p className={styles.title}>{t('tariff_rules')}</p>

        {rules && (
          <div className={styles.detailWrapper}>
            <div
              className={styles.desc}
              dangerouslySetInnerHTML={{
                __html: getCleanHTML(rules),
              }}
            />
          </div>
        )}
      </div>

      {!rules && !isLoading ? (
        <EmptyResults
          image={
            <img
              src={EmptyImage}
              alt=""
              className={styles.empty}
            />
          }
          title={t('no_data')}
          subTitle={t('empty_des_tariff')}
        />
      ) : null}
    </div>
  )
}

export default TariffPop
